import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, FileText, Calendar, Package, Download, Database, CloudOff } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardHome = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    patients: 0,
    reports: 0,
    appointments: 0,
    resources: 0,
  });
  const [loading, setLoading] = useState(true);
  const [backupSettings, setBackupSettings] = useState({
    auto_backup_enabled: false,
    frequency: 'manual',
    last_backup: null
  });
  const [downloadingBackup, setDownloadingBackup] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchBackupSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStats = async () => {
    try {
      const [patientsRes, reportsRes, appointmentsRes, resourcesRes] = await Promise.all([
        axios.get(`${API}/patients`, { withCredentials: true }),
        axios.get(`${API}/reports`, { withCredentials: true }),
        axios.get(`${API}/appointments`, { withCredentials: true }),
        axios.get(`${API}/resources`, { withCredentials: true }),
      ]);
      setStats({
        patients: patientsRes.data.length,
        reports: reportsRes.data.length,
        appointments: appointmentsRes.data.length,
        resources: resourcesRes.data.length,
      });
    } catch (error) {
      toast.error('Error al cargar estadísticas');
    } finally {
      setLoading(false);
    }
  };

  const fetchBackupSettings = async () => {
    try {
      const { data } = await axios.get(`${API}/backup/settings`, { withCredentials: true });
      setBackupSettings(data);
    } catch (error) {
      console.error('Error loading backup settings:', error);
    }
  };

  const handleDownloadBackup = async () => {
    try {
      setDownloadingBackup(true);
      const response = await axios.post(
        `${API}/backup/download`,
        {},
        {
          withCredentials: true,
          responseType: 'blob'
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rekord_backup_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Mark backup as completed
      await axios.post(`${API}/backup/mark-completed`, {}, { withCredentials: true });
      
      toast.success('Backup descargado exitosamente');
      fetchBackupSettings(); // Refresh to show last backup date
    } catch (error) {
      toast.error('Error al descargar backup');
    } finally {
      setDownloadingBackup(false);
    }
  };

  const handleUpdateBackupSettings = async (field, value) => {
    try {
      const newSettings = { ...backupSettings, [field]: value };
      await axios.put(`${API}/backup/settings`, newSettings, { withCredentials: true });
      setBackupSettings(newSettings);
      toast.success('Configuración actualizada');
    } catch (error) {
      toast.error('Error al actualizar configuración');
    }
  };

  const statCards = [
    {
      title: 'Pacientes',
      value: stats.patients,
      icon: Users,
      link: '/dashboard/patients',
      color: '#5A7D6C',
    },
    {
      title: 'Informes',
      value: stats.reports,
      icon: FileText,
      link: '/dashboard/reports',
      color: '#D98A6C',
    },
    {
      title: 'Citas Programadas',
      value: stats.appointments,
      icon: Calendar,
      link: '/dashboard/appointments',
      color: '#5A7D6C',
    },
    {
      title: 'Recursos de Sala',
      value: stats.resources,
      icon: Package,
      link: '/dashboard/resources',
      color: '#D98A6C',
    },
  ];

  if (loading) return <div>Cargando...</div>;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-semibold" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
          Bienvenido, {user?.name}
        </h1>
        <p className="text-lg mt-2" style={{ color: '#718096' }}>
          {user?.institution_name}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => (
          <Link key={stat.title} to={stat.link}>
            <Card className="p-6 hover:-translate-y-1 transition-transform duration-200 cursor-pointer">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium" style={{ color: '#718096' }}>
                    {stat.title}
                  </p>
                  <p className="text-3xl font-semibold mt-2" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
                    {stat.value}
                  </p>
                </div>
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${stat.color}20` }}
                >
                  <stat.icon size={24} style={{ color: stat.color }} />
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Backup Section - NEW */}
        <Card className="p-8" style={{ backgroundColor: '#5A7D6C' }}>
          <div className="flex items-center gap-3 mb-4">
            <Database size={28} className="text-white" />
            <h2 className="text-xl font-semibold text-white" style={{ fontFamily: 'Manrope' }}>
              Backup de Datos
            </h2>
          </div>

          <p className="text-white/90 mb-6 text-sm">
            Protege tu información realizando backups periódicos. Descarga una copia completa de todos tus datos.
          </p>

          {backupSettings.last_backup && (
            <p className="text-white/80 text-xs mb-4">
              Último backup: {new Date(backupSettings.last_backup).toLocaleString('es-ES')}
            </p>
          )}

          <div className="space-y-4">
            <Button
              onClick={handleDownloadBackup}
              disabled={downloadingBackup}
              className="w-full bg-white text-[#5A7D6C] hover:bg-white/90 font-medium flex items-center gap-2 justify-center"
            >
              <Download size={18} />
              {downloadingBackup ? 'Descargando...' : 'Descargar Backup Ahora'}
            </Button>

            <div className="border-t border-white/20 pt-4">
              <Label className="text-white text-sm mb-2 block">Frecuencia de Recordatorios</Label>
              <Select
                value={backupSettings.frequency}
                onValueChange={(value) => handleUpdateBackupSettings('frequency', value)}
              >
                <SelectTrigger className="w-full bg-white/10 border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual (cuando yo decida)</SelectItem>
                  <SelectItem value="daily">Diariamente</SelectItem>
                  <SelectItem value="weekly">Semanalmente</SelectItem>
                  <SelectItem value="monthly">Mensualmente</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-white/70 text-xs mt-2">
                {backupSettings.frequency === 'manual' 
                  ? 'Realiza backups cuando lo necesites'
                  : `Te recordaremos hacer backup ${backupSettings.frequency === 'daily' ? 'todos los días' : backupSettings.frequency === 'weekly' ? 'cada semana' : 'cada mes'}`
                }
              </p>
            </div>

            <div className="bg-white/10 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <CloudOff size={16} className="text-white/70 mt-1 flex-shrink-0" />
                <p className="text-white/80 text-xs">
                  <strong>Nota:</strong> El backup se descarga como archivo JSON en tu computadora. 
                  Para backup automático en la nube, configura Google Drive en Configuración → Institución.
                </p>
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-8">
          <h2 className="text-xl font-semibold mb-4" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
            Accesos Rápidos
          </h2>
          <div className="space-y-3">
            <Link
              to="/dashboard/reports"
              className="block p-4 rounded-lg border border-[#E5E2DC] hover:bg-[#F9F8F6] transition-colors"
            >
              <p className="font-medium" style={{ color: '#2D3748' }}>
                Crear Nuevo Informe
              </p>
              <p className="text-sm mt-1" style={{ color: '#718096' }}>
                Registra una nueva sesión Snoezelen
              </p>
            </Link>
            <Link
              to="/dashboard/appointments"
              className="block p-4 rounded-lg border border-[#E5E2DC] hover:bg-[#F9F8F6] transition-colors"
            >
              <p className="font-medium" style={{ color: '#2D3748' }}>
                Programar Cita
              </p>
              <p className="text-sm mt-1" style={{ color: '#718096' }}>
                Agenda una nueva sesión
              </p>
            </Link>
            <Link
              to="/dashboard/patients"
              className="block p-4 rounded-lg border border-[#E5E2DC] hover:bg-[#F9F8F6] transition-colors"
            >
              <p className="font-medium" style={{ color: '#2D3748' }}>
                Agregar Paciente
              </p>
              <p className="text-sm mt-1" style={{ color: '#718096' }}>
                Registra un nuevo paciente
              </p>
            </Link>
          </div>
        </Card>

        <Card className="p-8" style={{ backgroundColor: '#5A7D6C' }}>
          <h2 className="text-xl font-semibold mb-4" style={{ color: '#2D3748', fontFamily: 'Manrope' }}>
            Sobre REKORD
          </h2>
          <p className="text-white/90 mb-4">
            REKORD es el sistema profesional de gestión e informes para salas multisensoriales Snoezelen,
            diseñado para proporcionar un entorno terapéutico controlado que estimula los sentidos y
            promueve el bienestar emocional y físico de los pacientes.
          </p>
          <p className="text-white/90">
            Este sistema te permite gestionar tus sesiones, pacientes y recursos de manera profesional y
            organizada.
          </p>
        </Card>
      </div>
    </div>
  );
};

export default DashboardHome;
